<template>
  <!--
    TagNameCellElementLite, multi-root.
    Имя тега (Category Name + DestType Tag) с кнопкой правки в верхнем-правом углу
    ЯЧЕЙКИ (кнопка позиционируется абсолютно, якорь — .qtl-cell с position: relative).
    Редактор имени — инлайн-поповер на MostaContextMenu (как у статуса тега в
    TagCellElementLite), без q-* и легаси. Сохранение — aux.updateSingleTagName.
  -->
  <span class="qtl-tagname" v-html="renderedHtml" />

  <button
    ref="editBtnRef"
    class="qtl-tagname-edit-btn"
    type="button"
    :aria-label="t('edit_label')"
    @click.stop="openEditMenu"
  >
    <q-icon name="edit" size="16px" />
  </button>

  <mosta-context-menu
    :visible="menuOpen"
    :position="menuPos"
    @close="menuOpen = false"
  >
    <div class="qtl-tagname-popover">
      <textarea
        ref="inputRef"
        class="qtl-tagname-popover__input"
        rows="1"
        :placeholder="t('name')"
        v-model="tempName"
        @keydown.esc="menuOpen = false"
        @keydown.enter.prevent="applyChanges"
      />
      <button
        type="button"
        class="qtl-tagname-popover__apply"
        :disabled="!tempName"
        :aria-label="t('apply_changes')"
        @click="applyChanges"
      >
        <q-icon name="done" size="18px" />
      </button>
    </div>
  </mosta-context-menu>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import type { CellDataSimple, TableDataCell } from "@/models/interfaces/entity-settings/querytable/querytable";
import { auxStore } from "@/store/auxillary";
import { i18n } from "@/i18n";
import { formatSimpleHtml } from "./cellFormat";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";

const props = defineProps<{
  element: TableDataCell;
  entityId?: number;
}>();

const emit = defineEmits<{
  (e: "refreshNeeded"): void;
}>();

const t = (k: string) => i18n.global.t(k);
const aux = auxStore();

const renderedHtml = computed(() => formatSimpleHtml(props.element, t));

// Исходное имя берём из первого значения ячейки (имя тега — простое строковое поле).
const initialName = String((props.element.Data?.[0] as CellDataSimple)?.Value ?? "");
const tempName = ref<string>(initialName);

const menuOpen = ref(false);
const menuPos = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });
const editBtnRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLTextAreaElement | null>(null);

function openEditMenu(ev: MouseEvent) {
  // Поповер у нижнего-правого угла кнопки (как было anchor="bottom end").
  const rect = editBtnRef.value?.getBoundingClientRect();
  if (rect) {
    menuPos.value = { top: `${rect.bottom + 2}px`, left: `${rect.right - 260}px` };
  } else {
    menuPos.value = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
  }
  // На открытии возвращаем актуальное имя и ставим фокус в поле.
  tempName.value = String((props.element.Data?.[0] as CellDataSimple)?.Value ?? "");
  menuOpen.value = true;
  nextTick(() => inputRef.value?.focus());
}

async function applyChanges() {
  if (!props.entityId || !tempName.value) return;
  const ok = (await aux.updateSingleTagName({
    Id: props.entityId,
    NewName: tempName.value,
  } as any)) as boolean;
  if (ok) {
    menuOpen.value = false;
    emit("refreshNeeded");
  }
}
</script>

<style lang="scss" scoped>
.qtl-tagname {
  display: inline;
  word-break: normal;
  overflow-wrap: anywhere;
  font-size: 13px;
}

.qtl-tagname-edit-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 3;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #424242; /* grey-8, как в легаси */
  padding: 2px;
  border-radius: 3px;
  line-height: 0;
  /* Прячем по умолчанию, показываем при наведении на строку (display-buttons-on-hover). */
  opacity: 0;
}

.qtl-tagname-edit-btn:hover,
.qtl-tagname-edit-btn:focus {
  opacity: 1;
  color: #3F884F;
}

:deep(.qtl-row:hover) .qtl-tagname-edit-btn {
  opacity: 1;
}

.qtl-tagname-popover {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 8px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.qtl-tagname-popover__input {
  min-width: 200px;
  max-width: 360px;
  resize: vertical;
  font-size: 13px;
  padding: 4px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-family: inherit;
}

.qtl-tagname-popover__apply {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #333;
  padding: 4px;
  line-height: 0;
}

.qtl-tagname-popover__apply:hover {
  color: #333333;
}

.qtl-tagname-popover__apply:disabled {
  color: #bbb;
  cursor: default;
}
</style>
